import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {useContentMgr, useRequestClass} from 'coreHooks';
import {ECMSMode} from 'coreStore/data/configurations/types';
import {getCpaConnectionInfo} from 'coreUtils/livePreview';
import {dateToDateTimeLocal} from 'coreUtils/utils';

import CMSPreviewSection from './Subcomponents/CMSPreviewSection';
import {clearCpaConnection} from './Subcomponents/CPAConnectionSection/api';
import StorefrontToolkitSection from './Subcomponents/StorefrontToolkitSection';
import {IStorefrontToolkitRef} from './Subcomponents/StorefrontToolkitSection/IStorefrontToolkitSection';
import {clearScheduledDate, getScheduledDate, persistScheduledDate, toISOWithoutMs} from './constants';
import {IDevToolkitProps} from './IDevToolkit';
import devToolkitMessages from './messages';

import styles from './DevToolkit.module.scss';

const DevToolkit = ({
	isCMSPreviewEnabled,
	isToolkitEnabled,
	iconName = 'filter',
	cmsFieldPropKey = 'label',
	onToolkitSubmit,
	components,
	renderCustomerGroupSelector,
}: IDevToolkitProps) => {
	const {Icon} = components;
	const intl = useIntl();
	const contentMgr = useContentMgr();
	const {instanceKey} = useRequestClass();
	const storefrontRef = useRef<IStorefrontToolkitRef>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date | null>(getScheduledDate);
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => setIsMounted(true), []);

	const togglePanel = useCallback(() => setIsOpen((prev) => !prev), []);
	const closePanel = useCallback(() => setIsOpen(false), []);

	const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value) {
			const date = new Date(e.target.value);
			setSelectedDate(date);
			persistScheduledDate(date);
		} else {
			setSelectedDate(null);
			clearScheduledDate();
		}
	}, []);

	const handleApplyPreview = useCallback(() => {
		const storefrontState = storefrontRef.current?.getCurrentPayload();
		const dateTime = selectedDate ? toISOWithoutMs(selectedDate) : null;

		if (selectedDate) {
			persistScheduledDate(new Date(dateTime!));
		} else {
			clearScheduledDate();
		}

		onToolkitSubmit?.({
			groupId: storefrontState?.groupId ?? null,
			sourceCode: storefrontState?.sourceCode ?? null,
			dateTime,
		});

		window.location.reload();
	}, [selectedDate, onToolkitSubmit]);

	const handleResetAll = useCallback(async () => {
		if (isCMSPreviewEnabled) {
			contentMgr.changeMode(ECMSMode.LIVE);

			if (getCpaConnectionInfo()) {
				await clearCpaConnection();
			}
		}

		setSelectedDate(null);
		clearScheduledDate();
		storefrontRef.current?.reset();
		onToolkitSubmit?.({groupId: null, sourceCode: null, dateTime: null});
		window.location.reload();
	}, [isCMSPreviewEnabled, contentMgr, onToolkitSubmit]);

	if (!isMounted || (!isCMSPreviewEnabled && !isToolkitEnabled)) {
		return null;
	}

	const cpaConnection = isCMSPreviewEnabled ? getCpaConnectionInfo() : null;

	return (
		<>
			{!isOpen && (
				<button onClick={togglePanel} className={styles['dev-toolkit__toggle']}>
					<Icon name={iconName} width={25} height={25} />
				</button>
			)}
			<div
				className={cn(styles['dev-toolkit'], {
					[styles['dev-toolkit--visible']]: isOpen,
				})}
			>
				<div className={styles['dev-toolkit__header']}>
					<div className={styles['dev-toolkit__logo']}>TK</div>
					<p className={styles['dev-toolkit__title']}>
						{intl.formatMessage(devToolkitMessages.title)}
					</p>
					<span className={styles['dev-toolkit__env']}>{instanceKey}</span>
					{cpaConnection && (
						<span className={styles['dev-toolkit__cpa']} title={cpaConnection.cmsUrl}>
							{['CPA', cpaConnection.host, cpaConnection.environmentId].filter(Boolean).join(' · ')}
						</span>
					)}
					<button className={styles['dev-toolkit__close']} onClick={closePanel}>
						<Icon name="close" width={16} height={16} />
					</button>
				</div>

				<div className={styles['dev-toolkit__body']}>
					<div className={styles['dev-toolkit__datepicker']}>
						<label htmlFor="effective-datetime">
							{intl.formatMessage(devToolkitMessages.effectiveDateTimeLabel)}
							<span className={styles['dev-toolkit__shared-badge']}>
								{intl.formatMessage(devToolkitMessages.sharedBadge)}
							</span>
						</label>
						<input
							id="effective-datetime"
							type="datetime-local"
							name="effective-datetime"
							value={selectedDate ? dateToDateTimeLocal(selectedDate) : ''}
							onChange={handleDateChange}
						/>
						<p className={styles['dev-toolkit__datepicker-subtitle']}>
							{intl.formatMessage(devToolkitMessages.dateTimeSharedSubtitle)}
						</p>
					</div>

					{isCMSPreviewEnabled && (
						<CMSPreviewSection
							cmsFieldPropKey={cmsFieldPropKey}
							components={{
								Button: components.Button,
								Field: components.Field,
							}}
							togglePanel={togglePanel}
						/>
					)}

					{isToolkitEnabled && onToolkitSubmit && (
						<StorefrontToolkitSection
							ref={storefrontRef}
							selectedDate={selectedDate}
							onToolkitSubmit={onToolkitSubmit}
							renderCustomerGroupSelector={renderCustomerGroupSelector}
						/>
					)}
				</div>

				<div className={styles['dev-toolkit__footer']}>
					<button className={styles['dev-toolkit__btn-reset']} onClick={handleResetAll}>
						{intl.formatMessage(devToolkitMessages.resetAll)}
					</button>
					<button className={styles['dev-toolkit__btn-primary']} onClick={handleApplyPreview}>
						{intl.formatMessage(devToolkitMessages.applyPreview)}
					</button>
				</div>
			</div>
		</>
	);
};

export default DevToolkit;
